import { useMemo } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Button } from "@components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";
import { Input } from "@components/ui/input";
import { AddressBlock } from "@shared-kernel/primary/shared/address-autocomplete/address-block";
import { LongTextField } from "@shared-kernel/primary/shared/long-text-field/long-text-field";
import { Loader2, FileText, Building2, GraduationCap, Settings } from "lucide-react";
import { SpeakerList } from "../speaker-list.content";
import { StudentDisplayCard } from "@academy-context/primary/shared/external-provider-batch-display/student-display-card.content";
import { ProviderInformationFormSection } from "@academy-context/shared/external-provider-batch/components/provider-information-form-section.content";
import { TrainingDetailsFormSection } from "@academy-context/shared/external-provider-batch/components/training-details-form-section.content";
import { commonAdapters } from "src/common-adapters";
import type { ExternalProviderDashboardVM } from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import type { Uploader } from "@shared-kernel/primary/shared/api-errors/use-field-upload";
import type { RequestState } from "@redux/state/shared-types";
import type { FormSubmit } from "@shared-kernel/primary/shared/api-errors/action-form";
import type { RequestDataFormInputs } from "../../form-validation/request-data-form.schema";

interface Props {
  data: ExternalProviderDashboardVM;
  form: UseFormReturn<RequestDataFormInputs>;
  onSubmit: FormSubmit;
  onUploadSpeakerResume: (rowIndex: number, file: File) => void;
  // Liste de pièces jointes : chaque fichier garde son refus (exception Uploader)
  onUploadAttachment: Uploader;
  uploadProcessing: RequestState;
}

export const RequestDataSection = ({ data, form, onSubmit, onUploadSpeakerResume, onUploadAttachment, uploadProcessing }: Props) => {
  const { dateProvider } = commonAdapters;
  const now = useMemo(() => dateProvider!.now(), [dateProvider]);

  const {
    control,
    formState: { isSubmitting },
  } = form;

  return (
    <div className="space-y-6">
      {/* Student Info (Read-only) */}
      <StudentDisplayCard student={data.student} variant="provider" />

      {/* Editable Form */}
      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Provider Information */}
          <div className="space-y-4">
            <h4 className="flex items-center gap-2 font-semibold text-white">
              <Building2 size={16} />
              Informations prestataire
            </h4>
            <div className="space-y-4 rounded-lg bg-card p-4">
              <ProviderInformationFormSection control={control} />
            </div>
          </div>

          {/* Training Details */}
          <div className="space-y-4">
            <h4 className="flex items-center gap-2 font-semibold text-white">
              <GraduationCap size={16} />
              Détails de la formation
            </h4>
            <div className="space-y-4 rounded-lg bg-card p-4">
              <TrainingDetailsFormSection
                control={control}
                now={now}
                onUploadAttachment={onUploadAttachment}
                websiteUrlReadOnly
                attachmentsHeader={
                  <div className="space-y-3 border-t border-muted-foreground/20 pt-4">
                    <h6 className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <FileText size={16} />
                      Documents fournis par l'élève
                    </h6>
                    <p className="text-sm text-muted-foreground">
                      Pour accélérer le traitement de la demande, veuillez fournir un maximum de documents relatifs à la formation.
                    </p>
                  </div>
                }
              />
            </div>
          </div>

          {/* Additional Required Fields */}
          <div className="space-y-4">
            <h4 className="flex items-center gap-2 font-semibold text-white">
              <Settings size={16} />
              Informations complémentaires requises
            </h4>
            <div className="space-y-4 rounded-lg bg-card p-4">
              {/* Legal Representative */}
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={control}
                  name="legalRepresentativeFirstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prénom du représentant légal</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="legalRepresentativeLastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom du représentant légal</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Training Location */}
              <AddressBlock
                fields={{ postalAddress: "trainingLocation", addressComplement: "trainingAddressComplement", country: "trainingCountry" }}
                addressLabel="Lieu de formation"
                addressSubtitle="Veillez à saisir une adresse précise : elle sera utilisée dans les convocations envoyées aux élèves."
              />

              {/* Equipment Description */}
              <LongTextField
                control={control}
                name="equipmentDescription"
                label="Description des moyens techniques et pédagogiques"
                description="Merci de préciser les équipements techniques et le matériel présents dans la salle ou le studio (piano, pupitre, micro, tabouret, etc.)."
              />

              {/* Speakers List */}
              <SpeakerList onUpload={onUploadSpeakerResume} uploadProcessing={uploadProcessing} />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button type="submit" disabled={isSubmitting || uploadProcessing === "pending"}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                "Valider et soumettre"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
